import React, { useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import v4 from 'uuid/v4';
import {
  addInputField,
  getFormEditorControlData,
  getSelectedFormEditorForm,
  selectInputField,
  setInputFieldOpacity,
} from '../../store/slices/formEditorSlice';
import { saveFormEditorFormThunk } from '../../store/thunks/formEditorThunks';
import { SetNameForForm } from './SetNameForForm/SetNameForForm';
import { BusinessIdToForm } from './SetBusinessIdToForm/SetBusinessIdToForm';
import { convertPdfToImage } from '../../utils/convertPdfToImage';

export const LeftColumnWidgets = ({ selectedPageIndex = 0 }) => {
  const dispatch = useDispatch();
  const selectedForm = useSelector(getSelectedFormEditorForm);
  const controlData = useSelector(getFormEditorControlData);

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (file && file.type !== 'application/pdf') {
      if (e.target) {
        e.target.value = '';
      }
      return;
    }

    if (file) {
      try {
        const imageBlobArray = await convertPdfToImage(file);

        let formEditorFormDraft = { id: v4(), businessId: null };
        const fileIds = [];
        for (let imageBlob of imageBlobArray) {
          fileIds.push(`${v4()}`);
        }

        dispatch(
          saveFormEditorFormThunk({
            formEditorFormDraft,
            fileIds: fileIds,
            fileBinaryArray: imageBlobArray,
          })
        );
      } catch (error) {
        console.error('Error converting PDF to image:', error);
      }
    }

    if (e.target) {
      e.target.value = '';
    }
  };

  const addNewWindow = () => {
    const newItem = {
      id: v4(),
      x: 250,
      y: 400,
      width: 100,
      height: 30,
      parentTitle: '',
      text: '',
      title: 'Uusi',
      type: null,
      textSize: 14,
      originalIndex: (() => {
        const currentPageFields = selectedForm?.inputFields?.[selectedPageIndex] || [];
        const maxOriginalIndex = Math.max(0, ...currentPageFields.map(field => field.originalIndex || 0));
        return maxOriginalIndex + 1;
      })(), 
    };

    dispatch(
      addInputField({ inputField: newItem, pageIndex: selectedPageIndex })
    );
  };

  const setOpacity = (opacity) => {
    dispatch(setInputFieldOpacity(opacity));
  };

  const styles = useMemo(() => {
    const card = {
      backgroundColor: '#005a7f',
      border: '1px solid rgba(255, 255, 255, 0.08)',
      padding: 14,
      backdropFilter: 'blur(12px)',
      WebkitBackdropFilter: 'blur(12px)',
    };
    const label = {
      fontSize: 11,
      letterSpacing: 0.4,
      textTransform: 'uppercase',
      color: 'rgba(255,255,255,0.6)',
      marginBottom: 6,
    };
    const input = {
      width: '100%',
      padding: '10px 12px',
      border: '1px solid rgba(255,255,255,0.15)',
      backgroundColor: 'rgba(0,90,127,0.3)',
      color: '#fff !important',
      outline: 'none',
    };
    const btn = {
      border: 'none',
      padding: '9px 12px',
      color: '#0f1220',
      fontWeight: 600,
      cursor: 'pointer',
      background: 'linear-gradient(180deg, #79ffe1 0%, #4ad3ff 100%)',
      boxShadow: '0 6px 14px rgba(74, 211, 255, 0.25)',
    };
    const textBtn = {
      ...btn,
      background: 'transparent',
      color: '#d8e8ff',
      border: '1px solid rgba(216,232,255,0.25)',
      boxShadow: 'none',
    };

    return {
      page: {
        minHeight: '100vh',
        color: '#fff',
        padding: 16,
        fontFamily:
          'ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, "Helvetica Neue", Arial',
      },
      header: {
        height: 48,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: 10,
      },
      title: { fontSize: 18, fontWeight: 800 },
      layout: {
        display: 'grid',
        gridTemplateColumns: '280px 1fr 360px',
        gap: 12,
        height: 'calc(100vh - 48px - 16px*2 - 10px)',
      },
      column: {
        display: 'grid',
        gridAutoRows: 'min-content',
        alignContent: 'start',
        gap: 10,
        overflow: 'auto',
        paddingRight: 4,
      },
      card,
      label,
      input,
      btn,
      textBtn,

      toggleWrap: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
      },

      // center preview
      previewCard: { ...card, display: 'grid', gap: 10 },
      previewCanvasWrap: {
        display: 'flex',
        justifyContent: 'center',
        // remove big borders/padding here; the frame will be the exact A4 size
      },

      a4: {
        width: '100%',
        height: '100%',
        borderRadius: 8,
        background: '#0B7196',
        display: 'grid',
        placeItems: 'center',
        color: 'rgba(255,255,255,0.65)',
        position: 'relative',
        overflow: 'hidden',
      },

      overlay: {
        position: 'absolute',
        inset: 0,
        background: `rgba(74, 211, 255, ${1 - controlData.inputFieldOpacity})`,
        pointerEvents: 'none',
      },
      previewHeader: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
      },
      small: { fontSize: 12, color: 'rgba(255,255,255,0.6)' },

      // left specifics
      upload: {
        ...card,
        display: 'grid',
        gap: 8,
        textAlign: 'center',
        borderStyle: 'dashed',
        borderColor: 'rgba(255,255,255,0.12)',
        padding: 18,
      },
      chips: { display: 'flex', gap: 6, flexWrap: 'wrap' },
      chip: {
        padding: '6px 10px',
        borderRadius: 999,
        background: 'rgba(255,255,255,0.1)',
        border: '1px solid rgba(255,255,255,0.12)',
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        fontSize: 12,
      },
      chipX: {
        cursor: 'pointer',
        background: 'rgba(255,255,255,0.18)',
        borderRadius: 6,
        padding: '2px 6px',
      },

      // right: window list header
      propHeader: {
        ...card,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
      },
      windowList: { display: 'flex', gap: 8, flexWrap: 'wrap' },
      windowPill: (active) => ({
        padding: '6px 10px',
        borderRadius: 999,
        border: '1px solid rgba(255,255,255,0.18)',
        background: active ? 'rgba(74,211,255,0.2)' : 'transparent',
        color: active ? '#c9f5ff' : '#d8e8ff',
        cursor: 'pointer',
        fontSize: 12,
      }),
    };
  }, [controlData.inputFieldOpacity]);

  const onSelectInputField = (id) => {
    dispatch(selectInputField({ id }));
  };

  return (
    <>
      <div style={styles.column}>
        <div style={styles.card}>
          <div style={styles.label}>Lomakkeen nimi</div>
          <SetNameForForm></SetNameForForm>
        </div>

        {!selectedForm ? (
          <div style={styles.upload}>
            <div style={{ fontWeight: 700 }}>
              Aloita lataamalla PDF tiedosto
            </div>
            <div style={styles.small}>Valitse PDF tiedosto</div>
            <label style={{ ...styles.btn, display: 'inline-block' }}>
              <input
                type="file"
                accept="application/pdf"
                style={{ display: 'none' }}
                onChange={handleFileChange}
              />
              Valitse PDF
            </label>
          </div>
        ) : null}

        {selectedForm && selectedForm?.id ? (
          <div style={styles.card}>
            <div style={{ display: 'grid', gap: 8 }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: '#fff' }}>
                Lomakekentät
              </div>
              <div style={styles.windowList}>
                {selectedForm?.inputFields?.[selectedPageIndex]?.map(
                  (inputField) => (
                    <button
                      key={inputField.id}
                      style={styles.windowPill(
                        inputField.id === controlData.selectedInputFieldId
                      )}
                      onClick={() => onSelectInputField(inputField.id)}
                    >
                      {inputField.title}
                    </button>
                  )
                )}
              </div>
              <button style={styles.textBtn} onClick={addNewWindow}>
                + Lisää uusi lomakekenttä
              </button>
            </div>
          </div>
        ) : null}

        {selectedForm && selectedForm?.id ? (
          <div style={styles.card}>
            <div
              style={{
                fontSize: 14,
                fontWeight: 700,
                marginBottom: 8,
                color: '#fff',
              }}
            >
              Syöte ikkunoiden läpinäkyvyys
            </div>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr auto',
                gap: 8,
                alignItems: 'center',
              }}
            >
              <input
                type="range"
                min="0"
                max="1"
                step="0.01"
                value={controlData.inputFieldOpacity}
                onChange={(e) => setOpacity(parseFloat(e.target.value))}
                style={{ width: '100%', accentColor: '#4ad3ff' }}
              />
              <div style={{ color: '#fff' }}>
                {controlData.inputFieldOpacity}
              </div>
            </div>
          </div>
        ) : null}

        {selectedForm && selectedForm?.id ? (
          <div style={styles.card}>
            <div
              style={{
                fontSize: 14,
                fontWeight: 700,
                marginBottom: 8,
                color: '#fff',
              }}
            >
              Julkaise lomake yrityksille
            </div>

            <BusinessIdToForm></BusinessIdToForm>
          </div>
        ) : null}
      </div>
    </>
  );
};
